import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff, Sparkles, Pencil, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFieldConfig } from "@/contexts/FieldConfigContext";
import { FIELDS, CORE_LOCKED_KEYS, CUSTOM_FIELD_TYPES } from "@/lib/fields";

const HIDEABLE = FIELDS.filter((f) => !CORE_LOCKED_KEYS.includes(f.key) && !f.computed);

const emptyForm = { label: "", type: "text", options: "", money: false, percent: false, section: "custom", in_list: false, ai_extract: false, ai_hint: "" };

export function FieldManager() {
  const { hidden, custom, save } = useFieldConfig();
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingKey, setEditingKey] = useState(null);
  const [origType, setOrigType] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);

  const persist = async (nextHidden, nextCustom) => {
    setBusy(true);
    try {
      await save({ hidden: nextHidden, custom: nextCustom });
    } catch {
      toast.error("Couldn't save field settings.");
    } finally { setBusy(false); }
  };

  const toggleHidden = (key) => {
    const set = new Set(hidden);
    if (set.has(key)) set.delete(key); else set.add(key);
    persist([...set], custom);
  };

  const removeCustom = (key) => {
    persist(hidden, custom.filter((c) => c.key !== key));
    toast.success("Custom field removed");
  };

  const toggleCustomFlag = (key, flag) => {
    persist(hidden, custom.map((c) => c.key === key ? { ...c, [flag]: !c[flag] } : c));
  };

  const closeForm = () => { setAdding(false); setEditingKey(null); setOrigType(null); setForm(emptyForm); };

  const openEdit = (c) => {
    setForm({
      label: c.label, type: c.type, options: (c.options || []).join(", "),
      money: !!c.money, percent: !!c.percent, section: c.section || "custom",
      in_list: !!c.in_list, ai_extract: !!c.ai_extract, ai_hint: c.ai_hint || "",
    });
    setEditingKey(c.key); setOrigType(c.type); setAdding(true);
  };

  // Move a custom field up/down (reorders how they appear on the tour sheet).
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= custom.length) return;
    const next = [...custom];
    [next[idx], next[j]] = [next[j], next[idx]];
    persist(hidden, next);
  };
  const onDrop = (idx) => {
    if (dragIndex === null || dragIndex === idx) { setDragIndex(null); return; }
    const next = [...custom];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(idx, 0, moved);
    setDragIndex(null);
    persist(hidden, next);
  };

  const saveField = async () => {
    const label = form.label.trim();
    if (!label) { toast.error("Give the field a name."); return; }
    const item = {
      label, type: form.type, section: "custom",
      in_list: false, ai_extract: form.ai_extract, ai_hint: form.ai_hint.trim(),
    };
    if (form.type === "select") item.options = form.options.split(",").map((s) => s.trim()).filter(Boolean);
    if (form.type === "number") { item.money = form.money; item.percent = form.percent; }
    if (editingKey) {
      const typeChanged = origType && origType !== form.type;
      await persist(hidden, custom.map((c) => c.key === editingKey ? { ...item, key: editingKey } : c));
      if (typeChanged) toast.warning("Type changed — existing tour values are kept as-is and may display plainly.", { duration: 6000 });
      else toast.success("Field updated");
    } else {
      await persist(hidden, [...custom, item]);
      toast.success(`Added "${label}"`);
    }
    closeForm();
  };

  return (
    <div className="space-y-5" data-testid="field-manager">
      {/* Hide / show base fields */}
      <div>
        <p className="mb-2 text-xs font-600 uppercase tracking-wider text-zinc-500">Show / hide fields</p>
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {HIDEABLE.map((f) => {
            const isHidden = hidden.includes(f.key);
            return (
              <label key={f.key} className="flex items-center justify-between gap-3 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2" data-testid={`field-toggle-${f.key}`}>
                <span className="flex min-w-0 items-center gap-2 text-sm text-zinc-200">
                  {isHidden ? <EyeOff className="h-3.5 w-3.5 shrink-0 text-zinc-500" /> : <Eye className="h-3.5 w-3.5 shrink-0 text-[#E0575B]" />}
                  <span className="truncate">{f.label}</span>
                </span>
                <Switch checked={!isHidden} onCheckedChange={() => toggleHidden(f.key)} disabled={busy} className="data-[state=checked]:bg-[#9B2226]" data-testid={`field-toggle-switch-${f.key}`} />
              </label>
            );
          })}
        </div>
      </div>

      {/* Custom fields */}
      <div>
        <p className="mb-2 text-xs font-600 uppercase tracking-wider text-zinc-500">Your custom fields</p>
        {custom.length === 0 && !adding && (
          <p className="mb-2 rounded-lg border border-dashed border-zinc-800 px-3 py-3 text-sm text-zinc-500" data-testid="custom-fields-empty">No custom fields yet. Add one to track anything the tour sheet doesn't cover.</p>
        )}
        <div className="space-y-2">
          {custom.map((c, idx) => (
            <div
              key={c.key}
              draggable
              onDragStart={() => setDragIndex(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(idx)}
              className={`rounded-lg border bg-zinc-900 px-3 py-2.5 transition-colors ${dragIndex === idx ? "border-[#9B2226] opacity-60" : "border-zinc-800"}`}
              data-testid={`custom-field-row-${c.key}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="flex shrink-0 flex-col text-zinc-600">
                    <button onClick={() => move(idx, -1)} disabled={busy || idx === 0} className="hover:text-zinc-300 disabled:opacity-30" data-testid={`custom-field-up-${c.key}`} aria-label="Move up"><ChevronUp className="h-3.5 w-3.5" /></button>
                    <button onClick={() => move(idx, 1)} disabled={busy || idx === custom.length - 1} className="hover:text-zinc-300 disabled:opacity-30" data-testid={`custom-field-down-${c.key}`} aria-label="Move down"><ChevronDown className="h-3.5 w-3.5" /></button>
                  </span>
                  <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-zinc-600" aria-hidden="true" />
                  <div className="min-w-0">
                    <span className="block truncate text-sm font-600 text-zinc-100">{c.label}</span>
                    <span className="text-[11px] text-zinc-500">
                      {(CUSTOM_FIELD_TYPES.find((t) => t.value === c.type)?.label || c.type)}
                      {c.type === "number" && c.money ? " ($)" : ""}{c.type === "number" && c.percent ? " (%)" : ""}
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button onClick={() => openEdit(c)} disabled={busy} className="rounded-md p-1.5 text-zinc-500 transition-colors hover:text-zinc-200" data-testid={`custom-field-edit-${c.key}`} aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => removeCustom(c.key)} disabled={busy} className="rounded-md p-1.5 text-zinc-500 transition-colors hover:text-red-400" data-testid={`custom-field-delete-${c.key}`} aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5 pl-6">
                <label className="flex items-center gap-1.5 text-xs text-zinc-400"><Switch checked={!!c.ai_extract} onCheckedChange={() => toggleCustomFlag(c.key, "ai_extract")} className="scale-90 data-[state=checked]:bg-[#9B2226]" data-testid={`custom-field-ai-${c.key}`} /> Auto-fill from screenshot</label>
              </div>
            </div>
          ))}
        </div>

        {adding ? (
          <div className="mt-2 space-y-3 rounded-lg border border-[#9B2226]/40 bg-[#9B2226]/5 p-3" data-testid="custom-field-form">
            <p className="text-xs font-700 uppercase tracking-wider text-[#E0575B]">{editingKey ? "Edit field" : "New field"}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="mb-1 block text-xs text-zinc-400">Field name</Label>
                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Dietary notes" className="border-zinc-800 bg-zinc-900" data-testid="custom-field-label-input" />
              </div>
              <div>
                <Label className="mb-1 block text-xs text-zinc-400">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="border-zinc-800 bg-zinc-900" data-testid="custom-field-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-950">
                    {CUSTOM_FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`custom-field-type-${t.value}`}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.type === "select" && (
              <div>
                <Label className="mb-1 block text-xs text-zinc-400">Choices (comma-separated)</Label>
                <Input value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} placeholder="Small, Medium, Large" className="border-zinc-800 bg-zinc-900" data-testid="custom-field-options-input" />
              </div>
            )}
            {form.type === "number" && (
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-1.5 text-xs text-zinc-300"><Switch checked={form.money} onCheckedChange={(v) => setForm({ ...form, money: v, percent: v ? false : form.percent })} className="scale-90 data-[state=checked]:bg-[#9B2226]" data-testid="custom-field-money" /> Show as money ($)</label>
                <label className="flex items-center gap-1.5 text-xs text-zinc-300"><Switch checked={form.percent} onCheckedChange={(v) => setForm({ ...form, percent: v, money: v ? false : form.money })} className="scale-90 data-[state=checked]:bg-[#9B2226]" data-testid="custom-field-percent" /> Show as percent (%)</label>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-xs text-zinc-300"><Switch checked={form.ai_extract} onCheckedChange={(v) => setForm({ ...form, ai_extract: v })} className="scale-90 data-[state=checked]:bg-[#9B2226]" data-testid="custom-field-ai-new" /> <span className="flex items-center gap-1"><Sparkles className="h-3 w-3 text-[#E0575B]" /> Try to auto-fill from an uploaded tour sheet</span></label>
              {form.ai_extract && (
                <Input value={form.ai_hint} onChange={(e) => setForm({ ...form, ai_hint: e.target.value })} placeholder="Hint for the AI, e.g. 'the room number near the top'" className="border-zinc-800 bg-zinc-900 text-sm" data-testid="custom-field-hint-input" />
              )}
            </div>

            <div className="flex gap-2 pt-1">
              <Button onClick={saveField} disabled={busy} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="custom-field-save-btn">{editingKey ? "Save changes" : "Add field"}</Button>
              <Button variant="ghost" onClick={closeForm} className="text-zinc-400" data-testid="custom-field-cancel-btn">Cancel</Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" onClick={() => { closeForm(); setAdding(true); }} className="mt-2 border-zinc-700 bg-transparent hover:bg-zinc-800" data-testid="add-custom-field-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Add custom field
          </Button>
        )}
      </div>
    </div>
  );
}
